import React, { useMemo, useState } from 'react';
import {
  FileText,
  Calendar,
  TrendingUp,
  FileSpreadsheet,
  File,
  Loader2,
} from 'lucide-react';
import toast, { Toaster } from 'react-hot-toast';
import {
  downloadSettlementReport,
  SettlementReportFormat,
} from '../../services/MasterAdmin/accountantService';

const toDateInputValue = (date: Date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

const ReportsPage: React.FC = () => {
  const today = useMemo(() => toDateInputValue(new Date()), []);
  const monthStart = useMemo(() => {
    const now = new Date();
    return toDateInputValue(new Date(now.getFullYear(), now.getMonth(), 1));
  }, []);

  const [dateRange, setDateRange] = useState({ start: monthStart, end: today });
  const [downloadingKey, setDownloadingKey] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reports = [
    {
      id: 'daily-settlement',
      title: 'Daily Settlement Report',
      description: 'Daily breakdown of all vendor settlements with platform fees and GST',
      icon: FileText,
      formats: ['CSV', 'Excel', 'PDF'],
      supported: true,
    },
    {
      id: 'vendor-earnings',
      title: 'Vendor-wise Earnings Report',
      description: 'Complete earnings report for each vendor with service-wise breakdown',
      icon: TrendingUp,
      formats: ['CSV', 'Excel', 'PDF'],
      supported: false,
    },
    {
      id: 'gst-report',
      title: 'GST Report',
      description: 'Comprehensive GST collection and breakdown report for tax filing',
      icon: FileSpreadsheet,
      formats: ['Excel', 'PDF'],
      supported: false,
    },
    {
      id: 'platform-revenue',
      title: 'Platform Revenue Report',
      description: 'Total platform revenue, fees collected, and growth metrics',
      icon: TrendingUp,
      formats: ['CSV', 'Excel', 'PDF'],
      supported: false,
    },
    {
      id: 'payout-history',
      title: 'Payout History Report',
      description: 'Complete history of all payouts with UTR and bank details',
      icon: FileText,
      formats: ['CSV', 'Excel', 'PDF'],
      supported: false,
    },
    {
      id: 'ledger-export',
      title: 'Ledger Export',
      description: 'Export complete ledger entries with filters applied',
      icon: FileSpreadsheet,
      formats: ['CSV', 'Excel'],
      supported: false,
    },
  ];

  const validateDateRange = (): { startDate: string; endDate: string } | null => {
    if (!dateRange.start || !dateRange.end) {
      setError('Please select both start and end dates.');
      return null;
    }
    if (dateRange.start > dateRange.end) {
      setError('Start date must be on or before end date.');
      return null;
    }
    setError(null);
    return { startDate: dateRange.start, endDate: dateRange.end };
  };

  const handleDownloadSettlementReport = async (
    format: SettlementReportFormat,
    rangeOverride?: { startDate: string; endDate: string }
  ) => {
    const range = rangeOverride ?? validateDateRange();
    if (!range) return;

    const key = `daily-settlement-${format}`;
    setDownloadingKey(key);
    try {
      const { fileName, rowCount } = await downloadSettlementReport(format, range);
      toast.success(
        rowCount > 0
          ? `Downloaded ${fileName} (${rowCount} entries)`
          : `Downloaded ${fileName} (no entries in range)`
      );
    } catch (err: unknown) {
      const message =
        err instanceof Error ? err.message : 'Failed to download settlement report';
      setError(message);
      toast.error(message);
    } finally {
      setDownloadingKey(null);
    }
  };

  const handleGenerateReport = (reportId: string, format: string) => {
    if (reportId !== 'daily-settlement') {
      toast.error('This report type is not available yet.');
      return;
    }

    if (format === 'CSV') {
      toast.error('CSV export is not available yet. Use Excel or PDF.');
      return;
    }

    if (format === 'Excel') {
      void handleDownloadSettlementReport('excel');
      return;
    }

    if (format === 'PDF') {
      void handleDownloadSettlementReport('pdf');
    }
  };

  const applyPresetRange = (start: string, end: string) => {
    setDateRange({ start, end });
    setError(null);
  };

  const handleQuickToday = () => {
    const range = { startDate: today, endDate: today };
    applyPresetRange(today, today);
    void handleDownloadSettlementReport('excel', range);
  };

  const handleQuickThisMonth = () => {
    const range = { startDate: monthStart, endDate: today };
    applyPresetRange(monthStart, today);
    void handleDownloadSettlementReport('excel', range);
  };

  const handleQuickQuarter = () => {
    const now = new Date();
    const quarterStartMonth = Math.floor(now.getMonth() / 3) * 3;
    const start = toDateInputValue(new Date(now.getFullYear(), quarterStartMonth, 1));
    const range = { startDate: start, endDate: today };
    applyPresetRange(start, today);
    void handleDownloadSettlementReport('excel', range);
  };

  return (
    <div className="space-y-6">
      <Toaster position="top-right" />
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900 dark:text-white sm:text-3xl">
            Financial Reports
          </h2>
          <p className="mt-1 text-gray-600 dark:text-gray-400">
            Generate and download financial reports for accounting and compliance
          </p>
        </div>
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-4 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <div className="flex flex-col gap-4 lg:flex-row lg:items-end">
          <Calendar className="hidden h-5 w-5 shrink-0 text-gray-400 lg:block" />
          <div className="grid w-full grid-cols-1 gap-4 sm:grid-cols-2 lg:flex lg:items-end lg:gap-3">
            <div className="min-w-0">
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                Start Date
              </label>
              <input
                type="date"
                value={dateRange.start}
                onChange={(e) => setDateRange({ ...dateRange, start: e.target.value })}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <div className="min-w-0">
              <label className="mb-1 block text-sm font-medium text-gray-700 dark:text-gray-300">
                End Date
              </label>
              <input
                type="date"
                value={dateRange.end}
                onChange={(e) => setDateRange({ ...dateRange, end: e.target.value })}
                className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 focus:border-primary-500 focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
              />
            </div>
            <button
              type="button"
              onClick={() => validateDateRange()}
              className="w-full rounded-lg bg-primary-600 px-4 py-2 text-white transition-colors hover:bg-primary-700 sm:w-auto lg:mt-0"
            >
              Apply Filter
            </button>
          </div>
        </div>
        {error && (
          <p className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>
        )}
        <p className="mt-3 text-xs text-gray-500 dark:text-gray-400">
          Settlement report uses service completion date for the selected range.
        </p>
      </div>

      <div className="grid grid-cols-1 gap-6 md:grid-cols-2 lg:grid-cols-3">
        {reports.map((report) => {
          const Icon = report.icon;
          return (
            <div
              key={report.id}
              className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm transition-shadow hover:shadow-md dark:border-gray-700 dark:bg-gray-800"
            >
              <div className="mb-4 flex items-start justify-between">
                <div className="rounded-lg bg-primary-100 p-3 dark:bg-primary-900/30">
                  <Icon className="h-6 w-6 text-primary-600 dark:text-primary-400" />
                </div>
              </div>
              <h3 className="mb-2 text-lg font-semibold text-gray-900 dark:text-white">
                {report.title}
              </h3>
              <p className="mb-4 text-sm text-gray-600 dark:text-gray-400">
                {report.description}
              </p>

              <div className="border-t border-gray-200 pt-4 dark:border-gray-700">
                <p className="mb-2 text-xs font-medium text-gray-500 dark:text-gray-400">
                  Available Formats:
                </p>
                <div className="flex flex-wrap gap-2">
                  {report.formats.map((format) => {
                    const downloadKey = `${report.id}-${format === 'Excel' ? 'excel' : format === 'PDF' ? 'pdf' : format.toLowerCase()}`;
                    const isLoading = downloadingKey === downloadKey;
                    const isSettlementDownload =
                      report.id === 'daily-settlement' &&
                      (format === 'Excel' || format === 'PDF');

                    return (
                      <button
                        key={format}
                        type="button"
                        disabled={isLoading}
                        onClick={() => handleGenerateReport(report.id, format)}
                        className="flex items-center space-x-1 rounded-md bg-gray-100 px-3 py-1.5 text-xs font-medium text-gray-700 transition-colors hover:bg-primary-100 hover:text-primary-600 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-gray-700 dark:text-gray-300 dark:hover:bg-primary-900/30 dark:hover:text-primary-400"
                      >
                        {isLoading ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <>
                            {format === 'CSV' && <File className="h-3 w-3" />}
                            {format === 'Excel' && <FileSpreadsheet className="h-3 w-3" />}
                            {format === 'PDF' && <FileText className="h-3 w-3" />}
                          </>
                        )}
                        <span>{format}</span>
                        {isSettlementDownload && !isLoading && (
                          <span className="sr-only">Download settlement report</span>
                        )}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      <div className="rounded-lg border border-gray-200 bg-white p-6 shadow-sm dark:border-gray-700 dark:bg-gray-800">
        <h3 className="mb-4 text-lg font-semibold text-gray-900 dark:text-white">
          Quick Report Generation
        </h3>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <button
            type="button"
            onClick={handleQuickToday}
            disabled={downloadingKey !== null}
            className="rounded-lg border border-gray-200 p-4 text-left transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:hover:bg-gray-700/50"
          >
            <FileText className="mb-2 h-5 w-5 text-primary-600 dark:text-primary-400" />
            <p className="font-medium text-gray-900 dark:text-white">Today&apos;s Settlement</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Download today&apos;s settlement report (Excel)
            </p>
          </button>
          <button
            type="button"
            onClick={handleQuickThisMonth}
            disabled={downloadingKey !== null}
            className="rounded-lg border border-gray-200 p-4 text-left transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:hover:bg-gray-700/50"
          >
            <Calendar className="mb-2 h-5 w-5 text-primary-600 dark:text-primary-400" />
            <p className="font-medium text-gray-900 dark:text-white">This Month</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Monthly settlement summary (Excel)
            </p>
          </button>
          <button
            type="button"
            onClick={handleQuickQuarter}
            disabled={downloadingKey !== null}
            className="rounded-lg border border-gray-200 p-4 text-left transition-colors hover:bg-gray-50 disabled:opacity-60 dark:border-gray-700 dark:hover:bg-gray-700/50"
          >
            <TrendingUp className="mb-2 h-5 w-5 text-primary-600 dark:text-primary-400" />
            <p className="font-medium text-gray-900 dark:text-white">Quarterly Report</p>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Current quarter settlement report (Excel)
            </p>
          </button>
        </div>
      </div>
    </div>
  );
};

export default ReportsPage;
